import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Route, Routes } from 'react-router-dom';
import { Pagesection, Tabs, TabsPanel } from '@ktag/styleguide/dist/react';

import { PollDetails } from 'api/api.types';
import ApiContext from 'api/ApiContext';
import ProportionalElected from './ProportionalElected';
import ProportionalCandidates from './ProportionalCandidates';
import ProportionalProportionsNrw from './ProportionalProportionsNrw';
import useGetPreviousElectionId from '../utils/useGetPreviousElectionId';
import useGetProportionalElectionDetails from '../utils/useGetProportionalElectionDetails';
import ProportionalSeats from './ProportionalSeats/ProportionalSeats';
import ProportionalProportionsGrw from './ProportionalProportionsGrw';

interface Props {
  details: PollDetails;
}

const ProportionalElectionDetails = ({ details }: Props) => {
  const { t } = useTranslation('wap');
  const [previousFinalStatus, setPreviousFinalStatus] = useState(details.progress.final);

  const params = useParams<{
    '*': string;
    parentDate: string;
    id: string;
    title: string;
  }>();
  const [activeTab] = params['*']?.split('/') || [];
  const { parentDate, id, title } = params;
  const baseHash = `/${parentDate}/${id}/${title}`;

  const navigate = useNavigate();

  const previousElectionId = useGetPreviousElectionId(
    details.info.previous_issue_id,
    details.info.id,
    details.info.type,
  );

  const { getPoll } = useContext(ApiContext);

  const previousElectionResults = useQuery(
    ['poll', previousElectionId],
    () => getPoll(previousElectionId, true),
    {
      refetchInterval: false,
      cacheTime: Infinity,
      enabled: Boolean(previousElectionId),
    },
  );

  const {
    currentYear,
    prevYear,
    isBiProportional,
    isFinal,
    formattedParties,
    formattedCandidates,
    listResultsWithPrevResults,
    partyResultsWithPrevResults,
    listGroupResultsWithPrevResults,
  } = useGetProportionalElectionDetails(details, previousElectionResults.data?.data);

  useEffect(() => {
    if (isFinal !== previousFinalStatus) {
      setPreviousFinalStatus(true);
      navigate(baseHash);
    }

    if (isFinal || activeTab) return;

    navigate(`${baseHash}/waehleranteile`);
  }, [isFinal]);

  return (
    <>
      <Pagesection additionalClassNames="proportionalelectiondetails__tabs">
        <Tabs noBezel type="button-large" ariaLabel={t('proportional_tablist_level1')}>
          {isFinal && (
            <TabsPanel
              hash={baseHash}
              label={t('seats_distribution')}
              id="seats_distribution"
            />
          )}
          {isFinal && (
            <TabsPanel
              hash={`${baseHash}/gewaehlte`}
              label={t('elected_candidates')}
              id="elected_candidates"
            />
          )}
          <TabsPanel
            hash={`${baseHash}/waehleranteile`}
            label={t('election_proportions')}
            id="election_proportions"
          />
          <TabsPanel
            hash={`${baseHash}/kandidierende`}
            label={t('candidates')}
            id="candidates"
          />
        </Tabs>
      </Pagesection>
      <Routes>
        <Route
          path="/"
          element={
            <ProportionalSeats
              currentYear={currentYear}
              prevYear={prevYear}
              lists={
                isBiProportional
                  ? listGroupResultsWithPrevResults
                  : listResultsWithPrevResults
              }
              parties={partyResultsWithPrevResults}
              pollId={details.info.id}
              isBiProportional={isBiProportional}
              previousElectionId={previousElectionId}
              previousElectionResults={previousElectionResults?.data?.data}
            />
          }
        />
        <Route
          path="gewaehlte"
          element={
            <ProportionalElected
              candidates={formattedCandidates}
              isBiProportional={isBiProportional}
              pollId={details.info.id}
              previousElectionResults={previousElectionResults?.data?.data}
              currentYear={new Date(details.info.date).getFullYear()}
              statistics={
                details.results?.proportional_election_data?.statistics
                  ?.elected_candidates
              }
              formattedParties={formattedParties}
            />
          }
        />
        <Route
          path="waehleranteile"
          element={
            isBiProportional ? (
              <ProportionalProportionsGrw
                lists={listGroupResultsWithPrevResults}
                pollId={details.info.id}
                currentElectionYear={currentYear}
                prevElectionYear={prevYear}
                isFinal={isFinal}
                previousElectionId={previousElectionId}
                previousElectionResults={previousElectionResults?.data?.data}
                prevYear={prevYear}
              />
            ) : (
              <ProportionalProportionsNrw
                parties={partyResultsWithPrevResults}
                lists={listResultsWithPrevResults}
                pollId={details.info.id}
                currentElectionYear={currentYear}
                prevElectionYear={prevYear}
                isFinal={isFinal}
              />
            )
          }
        />
        <Route
          path="kandidierende"
          element={
            <ProportionalCandidates
              isBiProportional={isBiProportional}
              candidates={formattedCandidates}
              isFinal={isFinal}
              pollId={details.info.id}
            />
          }
        />
      </Routes>
    </>
  );
};

export default ProportionalElectionDetails;
